import React from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Card, Icon, RowsSkeletonLoader } from '@node-space/ui-components'
import { formatAmount } from 'pages/Rewards/utils'

export interface RewardsEmbeddedSummaryProps {
  currency: string
  userCurrency: string
  totalBalance: string
  totalEarnings: string
  hasBeenDisbursed: boolean
  isLoading: boolean
}

export const RewardsEmbeddedSummary = ({
  currency,
  userCurrency,
  totalBalance,
  totalEarnings,
  hasBeenDisbursed,
  isLoading,
}: RewardsEmbeddedSummaryProps) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Card variant="static" emphasis full>
        <Box flexCol gap={24}>
          <Box gap={8} flexCol>
            <p className="text-body-regular-small text-foreground-prominent">
              {t('rewards.embeddedCustomers.totalAllocatedBalance', { currency })}
            </p>
          </Box>

          <Box className="w-full h-6">
            <RowsSkeletonLoader rows={1} />
          </Box>
        </Box>
      </Card>
    )
  }

  return (
    <Card variant="static" emphasis full>
      <Box
        flexCol
        gap={24}
        justify="between"
        className={clsx('xl:flex-row', {
          'xl:justify-between xl:items-center': hasBeenDisbursed,
        })}
      >
        <Box gap={8} flexCol>
          <p className="text-body-regular-small text-foreground-prominent">
            {t('rewards.embeddedCustomers.totalAllocatedBalance', { currency })}
          </p>

          <Box flex align="center" gap={8} className="lg:justify-start">
            <Avatar name={userCurrency} type="currency" />
            <h1 className="text-h1 text-foreground-prominent">
              {formatAmount(Number.parseFloat(totalBalance))}
            </h1>
          </Box>

          <Box flex align="center" gap={8} className="lg:justify-start text-foreground-prominent">
            <Icon name="SketchLogo" size="medium" />
            <p className="text-body-strong text-foreground-subdued">
              {t('rewards.allTimeEarnings', {
                currency: userCurrency,
                amount: formatAmount(Number.parseFloat(totalEarnings)),
              })}
            </p>
          </Box>
        </Box>
        {hasBeenDisbursed && (
          <Box flexCol gap={8}>
            <p className="text-body-strong-large text-foreground-prominent">
              {t('rewards.embeddedCustomers.disbursed')}
            </p>
          </Box>
        )}
      </Box>
    </Card>
  )
}
